// components/projects/SignatureModal.js - Clean R2 Signature Implementation
import React, { useRef, useState } from 'react';
import { X, Save, Undo } from 'lucide-react';
import SignaturePad from 'react-signature-canvas';

const SignatureModal = ({ isOpen, onClose, onSave, title = "Customer Signature" }) => {
  const sigPadRef = useRef(null);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleClear = () => {
    sigPadRef.current?.clear();
    setError('');
  };

  const handleSave = async () => {
    if (sigPadRef.current?.isEmpty()) {
      setError('Please provide a signature');
      return;
    }
  
    try {
      setIsSaving(true);
      setError('');
      
      // ✅ Create signature data - backend will handle R2 conversion
      const signatureData = {
        base64: sigPadRef.current.getTrimmedCanvas().toDataURL('image/png'),
        date: new Date().toISOString()
      };
      
      console.log('💾 SignatureModal: Sending signature data:', {
        hasBase64: !!signatureData.base64,
        base64Length: signatureData.base64.length,
        date: signatureData.date
      });
      
      // Wait for the save operation to complete
      await onSave(signatureData);
      
      console.log('✅ SignatureModal: Signature saved successfully');
      
      // If we get here without errors, close the modal
      onClose();
    } catch (err) {
      console.error('❌ SignatureModal: Signature save error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save signature. Please try again.');
      
      // Keep the modal open so the user can try again
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  // Direct stopPropagation on the entire modal to prevent event bubbling
  return (
    <div 
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" 
      onClick={(e) => e.stopPropagation()}
    >
      <div 
        className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              if (!isSaving) onClose();
            }} 
            className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
            disabled={isSaving}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="border rounded-lg mb-4">
          <SignaturePad
            ref={sigPadRef}
            canvasProps={{
              className: "w-full h-64 signature-canvas"
            }}
            backgroundColor="rgb(255, 255, 255)"
          />
        </div>

        <div className="flex justify-between">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="flex items-center px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            disabled={isSaving}
          >
            <Undo className="h-4 w-4 mr-2" />
            Clear
          </button>
          <div className="space-x-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!isSaving) onClose();
              }}
              className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSave();
              }}
              className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Signature
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;